// apps/landing-page/src/components/search/useCatalogSearch.ts
// One lazy catalog fetch and one way to turn a query into rows, shared by the navbar
// combobox, the modal dialog and the /cari page.
'use client';

import { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslations } from 'next-intl';
import { catalogApi, type ExamType, type ProductCatalog } from '@/lib/api';
import { buildRows, MAX_PER_GROUP, type SearchRow, type Translate } from './searchRows';

type CatalogState =
  | { status: 'idle' | 'loading' | 'error' }
  | { status: 'ready'; products: ProductCatalog };

export interface CatalogSearch {
  status: CatalogState['status'];
  /** Starts the fetch the first time it is called; later calls do nothing. */
  ensureLoaded: () => void;
  retry: () => void;
  /** Exam family links, shown before anything is typed. */
  quickRows: SearchRow[];
  colorBySlug: Map<string, string | null>;
  /** Empty until the catalog is ready. */
  rowsFor: (query: string, limit?: number) => SearchRow[];
}

// Shared by every instance on the page (navbar combobox and dialog), so the catalog is
// requested once. Cleared on failure so a retry really retries.
let inflight: Promise<ProductCatalog> | null = null;
function fetchCatalog(): Promise<ProductCatalog> {
  if (!inflight) {
    // ponytail: client-side filter over the full catalog (~50 items, one 28 KB response, cached
    // 5 min server-side). Past a few hundred items, move this to a server endpoint backed by a
    // Postgres trigram index (pg_trgm) and send only the query.
    inflight = catalogApi.getProducts();
    inflight.catch(() => { inflight = null; });
  }
  return inflight;
}

export function useCatalogSearch({ examTypes, coursesGated }: { examTypes: ExamType[]; coursesGated: boolean }): CatalogSearch {
  const t = useTranslations('search');
  const [catalog, setCatalog] = useState<CatalogState>({ status: 'idle' });
  const started = useRef(false);

  const load = useCallback(() => {
    setCatalog({ status: 'loading' });
    fetchCatalog().then(
      (products) => setCatalog({ status: 'ready', products }),
      () => setCatalog({ status: 'error' }), // fetchApi already logged the cause; the UI offers a retry
    );
  }, []);

  const ensureLoaded = useCallback(() => {
    if (started.current) return;
    started.current = true;
    load();
  }, [load]);

  const quickRows = useMemo<SearchRow[]>(
    () => examTypes
      .filter((et) => et._count.modules > 0)
      .map((et) => ({
        id: `exam-${et.id}`,
        group: 'exams',
        title: et.name,
        context: t('examCount', { count: et._count.modules }),
        href: `/tests?examTypeSlug=${et.slug}`,
        familySlug: et.slug,
      })),
    [examTypes, t],
  );

  const colorBySlug = useMemo(() => new Map(examTypes.map((et) => [et.slug, et.color])), [examTypes]);

  const products = catalog.status === 'ready' ? catalog.products : null;
  const rowsFor = useCallback(
    (query: string, limit = MAX_PER_GROUP) =>
      products ? buildRows(products, query, { coursesGated, t: t as Translate, limit }) : [],
    [products, coursesGated, t],
  );

  return { status: catalog.status, ensureLoaded, retry: load, quickRows, colorBySlug, rowsFor };
}
